import React from 'react'
import { Link } from 'react-router-dom'

interface HeroProps {
  onStartClub: () => void
  user: { id: number; name: string; email: string } | null
  onSignOut: () => void
  onSignIn: () => void
}

export default function Hero({ onStartClub, user, onSignOut, onSignIn }: HeroProps) {
  return (
    <section className="relative overflow-hidden bg-gray-50">
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />

      {/* Header with sign in/out */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex justify-between items-center gap-4">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-full"></div>
            <span className="text-xl font-bold text-gray-700">BooksForUs</span>
          </div>

          {/* Sign in/out */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center group-hover:from-indigo-200 group-hover:to-purple-200 transition-all">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 font-medium">{user.name}</span>
                </Link>
                <button
                  onClick={onSignOut}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors px-3 py-2 rounded-lg hover:bg-gray-100"
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                onClick={onSignIn}
                className="text-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors px-4 py-2 rounded-lg font-medium shadow-sm"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24 sm:pt-20 sm:pb-32">
        <div className="text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">
            Read great books with
            <span className="block bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent pb-2">
              your favorite people
            </span>
          </h1>

          <p className="mt-6 text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Connect with friends over books you love. Share thoughts, have conversations, and discover your next great read together.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={onStartClub}
              className="group relative inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-all hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Your Club
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Visual Element - Bookshelf */}
        <div className="mt-20 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-transparent z-10" />
          <div className="relative bg-gradient-to-b from-gray-50 to-amber-50 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden p-8 sm:p-12">
            <div className="flex items-end justify-center gap-1 sm:gap-2 h-64">
              {/* Book spines */}
              <div className="w-8 sm:w-10 h-52 bg-gradient-to-br from-rose-300 to-rose-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-10 sm:w-12 h-56 bg-gradient-to-br from-blue-300 to-blue-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-6 sm:w-8 h-48 bg-gradient-to-br from-amber-300 to-amber-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-9 sm:w-11 h-56 bg-gradient-to-br from-purple-300 to-purple-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-8 sm:w-10 h-52 bg-gradient-to-br from-green-300 to-green-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-10 sm:w-12 h-60 bg-gradient-to-br from-pink-300 to-pink-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-7 sm:w-9 h-52 bg-gradient-to-br from-cyan-300 to-cyan-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-9 sm:w-11 h-56 bg-gradient-to-br from-indigo-300 to-indigo-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-8 sm:w-10 h-56 bg-gradient-to-br from-orange-300 to-orange-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-10 sm:w-12 h-52 bg-gradient-to-br from-teal-300 to-teal-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-7 sm:w-9 h-48 bg-gradient-to-br from-red-300 to-red-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-9 sm:w-11 h-56 bg-gradient-to-br from-lime-300 to-lime-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-8 sm:w-10 h-48 bg-gradient-to-br from-fuchsia-300 to-fuchsia-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-10 sm:w-12 h-56 bg-gradient-to-br from-sky-300 to-sky-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-7 sm:w-9 h-52 bg-gradient-to-br from-violet-300 to-violet-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-9 sm:w-11 h-60 bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-8 sm:w-10 h-52 bg-gradient-to-br from-yellow-300 to-yellow-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
              <div className="w-10 sm:w-12 h-56 bg-gradient-to-br from-rose-300 to-rose-400 rounded-t-sm shadow-md relative">
                <div className="absolute inset-x-0 top-2 h-px bg-white opacity-20"></div>
                <div className="absolute inset-x-0 bottom-2 h-px bg-black opacity-10"></div>
              </div>
            </div>
            {/* Shelf */}
            <div className="mt-2 h-3 bg-gradient-to-b from-amber-600 to-amber-700 rounded-sm shadow-lg"></div>
          </div>
        </div>
      </div>
    </section>
  )
}
